import * as THREE from 'three'

import type { BoneLocal, Mdl } from './mdl'

const ONE = new THREE.Vector3(1, 1, 1)
const NORMAL_EPSILON = 1000

/** GoldSrc bone euler (radians) → quaternion (Half-Life AngleQuaternion). */
export function angleQuaternion(
  rx: number,
  ry: number,
  rz: number,
): THREE.Quaternion {
  const sr = Math.sin(rx * 0.5)
  const cr = Math.cos(rx * 0.5)
  const sp = Math.sin(ry * 0.5)
  const cp = Math.cos(ry * 0.5)
  const sy = Math.sin(rz * 0.5)
  const cy = Math.cos(rz * 0.5)
  return new THREE.Quaternion(
    sr * cp * cy - cr * sp * sy,
    cr * sp * cy + sr * cp * sy,
    cr * cp * sy - sr * sp * cy,
    cr * cp * cy + sr * sp * sy,
  )
}

/** World matrix per bone from its local pos/rot (parents first). */
export function computeBoneWorld(
  mdl: Mdl,
  locals: BoneLocal[],
): THREE.Matrix4[] {
  const world: THREE.Matrix4[] = new Array(mdl.bones.length)
  const p = new THREE.Vector3()
  for (let i = 0; i < mdl.bones.length; i++) {
    const L = locals[i]
    const local = new THREE.Matrix4().compose(
      p.set(L.pos[0], L.pos[1], L.pos[2]),
      angleQuaternion(L.rot[0], L.rot[1], L.rot[2]),
      ONE,
    )
    const parent = mdl.bones[i].parent
    world[i] = parent >= 0 ? world[parent].clone().multiply(local) : local
  }
  return world
}

export interface MdlBuilt {
  group: THREE.Group
  /** Re-deform the mesh to a new pose (bone world matrices). */
  skin(boneWorld: THREE.Matrix4[]): void
}

interface SkinGroup {
  positions: Float32Array
  normals: Float32Array
  attr: THREE.BufferAttribute
  normalAttr: THREE.BufferAttribute
  srcModel: Uint8Array
  srcVert: Int32Array
  srcBone: Uint8Array
  mesh: THREE.Mesh
}

interface NormalBucket {
  x: number
  y: number
  z: number
  indices: number[]
}

/**
 * Build a re-poseable mesh from a parsed MDL (single-bone rigid skinning).
 * `worldUp=false` keeps GoldSrc (Z-up) space — used when attaching to a bone.
 */
export function buildMdlMesh(mdl: Mdl, worldUp = true): MdlBuilt {
  interface Acc {
    src: { model: number; vert: number; bone: number }[]
    uv: number[]
    texIndex: number
  }
  const accs = new Map<number, Acc>()
  for (let mi = 0; mi < mdl.models.length; mi++) {
    const model = mdl.models[mi]
    for (const mesh of model.meshes) {
      const texIndex = mdl.skinRefs[mesh.skinRef] ?? 0
      const tex = mdl.textures[texIndex]
      let a = accs.get(texIndex)
      if (!a) {
        a = { src: [], uv: [], texIndex }
        accs.set(texIndex, a)
      }
      for (const tv of mesh.tris) {
        a.src.push({ model: mi, vert: tv.vert, bone: model.vertBones[tv.vert] })
        a.uv.push(tv.s / tex.width, tv.t / tex.height)
      }
    }
  }

  const group = new THREE.Group()
  const groups: SkinGroup[] = []

  for (const a of accs.values()) {
    const n = a.src.length
    if (n === 0) continue
    const positions = new Float32Array(n * 3)
    const normals = new Float32Array(n * 3)
    const srcModel = new Uint8Array(n)
    const srcVert = new Int32Array(n)
    const srcBone = new Uint8Array(n)
    for (let i = 0; i < n; i++) {
      srcModel[i] = a.src[i].model
      srcVert[i] = a.src[i].vert
      srcBone[i] = a.src[i].bone
    }

    const geo = new THREE.BufferGeometry()
    // BufferAttribute (not Float32BufferAttribute) keeps OUR array — the typed
    // subclasses copy it, which would break per-frame skinning writes.
    const attr = new THREE.BufferAttribute(positions, 3)
    const normalAttr = new THREE.BufferAttribute(normals, 3)
    attr.setUsage(THREE.DynamicDrawUsage)
    normalAttr.setUsage(THREE.DynamicDrawUsage)
    geo.setAttribute('position', attr)
    geo.setAttribute('normal', normalAttr)
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(a.uv, 2))

    const tex = mdl.textures[a.texIndex]
    const dt = new THREE.DataTexture(
      tex.rgba,
      tex.width,
      tex.height,
      THREE.RGBAFormat,
    )
    dt.colorSpace = THREE.SRGBColorSpace
    dt.wrapS = THREE.RepeatWrapping
    dt.wrapT = THREE.RepeatWrapping
    dt.magFilter = THREE.LinearFilter
    dt.minFilter = THREE.LinearMipmapLinearFilter
    dt.generateMipmaps = true
    dt.anisotropy = 16
    dt.needsUpdate = true

    const material = new THREE.MeshStandardMaterial({
      map: dt,
      roughness: 0.74,
      metalness: 0.02,
      emissive: 0x18130d,
      emissiveIntensity: 0.16,
      side: THREE.DoubleSide,
    })
    if (tex.transparent) material.alphaTest = 0.5

    const mesh = new THREE.Mesh(geo, material)
    mesh.frustumCulled = false // re-skinned each frame; bounds change
    group.add(mesh)
    groups.push({
      positions,
      normals,
      attr,
      normalAttr,
      srcModel,
      srcVert,
      srcBone,
      mesh,
    })
  }

  const v = new THREE.Vector3()
  const skin = (boneWorld: THREE.Matrix4[]): void => {
    for (const g of groups) {
      const { positions, srcModel, srcVert, srcBone } = g
      const count = srcVert.length
      for (let i = 0; i < count; i++) {
        const model = mdl.models[srcModel[i]]
        const vi = srcVert[i] * 3
        v.set(
          model.vertices[vi],
          model.vertices[vi + 1],
          model.vertices[vi + 2],
        )
        v.applyMatrix4(boneWorld[srcBone[i]])
        if (worldUp) {
          positions[i * 3] = v.x // GoldSrc Z-up → three Y-up
          positions[i * 3 + 1] = v.z
          positions[i * 3 + 2] = -v.y
        } else {
          positions[i * 3] = v.x
          positions[i * 3 + 1] = v.y
          positions[i * 3 + 2] = v.z
        }
      }
      smoothVertexNormals(g.positions, g.normals)
      g.attr.needsUpdate = true
      g.normalAttr.needsUpdate = true
    }
  }

  return { group, skin }
}

function normalKey(positions: Float32Array, i: number): string {
  return (
    Math.round(positions[i] * NORMAL_EPSILON) +
    ',' +
    Math.round(positions[i + 1] * NORMAL_EPSILON) +
    ',' +
    Math.round(positions[i + 2] * NORMAL_EPSILON)
  )
}

function smoothVertexNormals(
  positions: Float32Array,
  normals: Float32Array,
): void {
  normals.fill(0)
  const buckets = new Map<string, NormalBucket>()

  for (let i = 0; i < positions.length; i += 3) {
    const key = normalKey(positions, i)
    let bucket = buckets.get(key)
    if (!bucket) {
      bucket = { x: 0, y: 0, z: 0, indices: [] }
      buckets.set(key, bucket)
    }
    bucket.indices.push(i)
  }

  for (let i = 0; i < positions.length; i += 9) {
    const ax = positions[i]
    const ay = positions[i + 1]
    const az = positions[i + 2]
    const bx = positions[i + 3]
    const by = positions[i + 4]
    const bz = positions[i + 5]
    const cx = positions[i + 6]
    const cy = positions[i + 7]
    const cz = positions[i + 8]

    const abx = bx - ax
    const aby = by - ay
    const abz = bz - az
    const acx = cx - ax
    const acy = cy - ay
    const acz = cz - az

    const nx = aby * acz - abz * acy
    const ny = abz * acx - abx * acz
    const nz = abx * acy - aby * acx

    for (const vi of [i, i + 3, i + 6]) {
      const bucket = buckets.get(normalKey(positions, vi))
      if (!bucket) continue
      bucket.x += nx
      bucket.y += ny
      bucket.z += nz
    }
  }

  for (const bucket of buckets.values()) {
    const len = Math.hypot(bucket.x, bucket.y, bucket.z) || 1
    const nx = bucket.x / len
    const ny = bucket.y / len
    const nz = bucket.z / len
    for (const i of bucket.indices) {
      normals[i] = nx
      normals[i + 1] = ny
      normals[i + 2] = nz
    }
  }
}
